import { useEffect, useState, type ReactNode } from 'react';
import { ArrowRight, Check, Clock, Copy, FileText, Flag, GraduationCap, Languages, Library, Link2, Mail, PenLine, RotateCcw, Sparkles, ThumbsDown, ThumbsUp, TriangleAlert, Wand2 } from 'lucide-react';
import { Button, Card, IconTile, Spinner } from '@/components/ui';
import { mailAssistantStream, submitAnswerFeedback, type MailAssistantResponse, type MailCreativity } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { cn, formatDate } from '@/lib/utils';
import { MailTrainingModal } from '@/components/features/mail/MailTrainingModal';
import { Maskot } from '@/components/features/maskot/Maskot';
import { MASKOT_RANNA } from '@/lib/maskot';

type MailTab = 'compose' | 'templates';

const CREATIVITY_STORAGE_KEY = 'mail-assistant-creativity';

const CREATIVITY_OPTIONS: Array<{ value: MailCreativity; label: string; description: string }> = [
  { value: 'saklig', label: 'Saklig', description: 'Konsekvent och formell — samma mail ger nästan samma svar' },
  { value: 'balanserad', label: 'Balanserad', description: 'Standard — varierad ton men strikt på fakta' },
  { value: 'personlig', label: 'Personlig', description: 'Mer fritt formulerat och varmare — som om en kollega skrev det' },
];

export function MailAssistantPage() {
  const { session } = useAuth();
  const [tab, setTab] = useState<MailTab>('compose');
  const [customerEmail, setCustomerEmail] = useState('');
  const [language, setLanguage] = useState<'sv' | 'en'>('sv');
  const [creativity, setCreativity] = useState<MailCreativity>(() => {
    if (typeof localStorage === 'undefined') return 'balanserad';
    const stored = localStorage.getItem(CREATIVITY_STORAGE_KEY);
    return stored === 'saklig' || stored === 'personlig' ? stored : 'balanserad';
  });

  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CREATIVITY_STORAGE_KEY, creativity);
    }
  }, [creativity]);
  const [result, setResult] = useState<MailAssistantResponse | null>(null);
  const [streamReply, setStreamReply] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<'up' | 'down' | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [trainingSaved, setTrainingSaved] = useState(false);

  const disabled = !customerEmail.trim() || loading;

  const generate = async () => {
    if (disabled) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setStreamReply('');
    setCopied(false);
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackSent(false);
    setFeedbackError(null);
    setTrainingSaved(false);
    try {
      let acc = '';
      await mailAssistantStream(
        {
          customerEmail: customerEmail.trim(),
          responseLanguage: language,
          creativity,
        },
        {
          onDelta: (text) => {
            acc += text;
            setStreamReply(acc);
          },
          onDone: (response) => setResult(response),
        },
        session?.access_token,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStreamReply(null);
      setLoading(false);
    }
  };

  const sendFeedback = async (rating: 'up' | 'down', commentOverride?: string) => {
    if (!result || feedbackSent || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await submitAnswerFeedback(
        {
          feature: 'mail_assistant',
          reference_id: result.logId,
          rating,
          comment: commentOverride ?? (rating === 'down' ? feedbackComment.trim() || null : null),
        },
        session?.access_token,
      );
      setFeedbackRating(rating);
      setFeedbackSent(true);
    } catch (e) {
      setFeedbackError((e as Error).message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const copyReply = async () => {
    if (!result?.reply) return;
    try {
      await navigator.clipboard.writeText(result.reply);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setCopied(false);
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackSent(false);
    setFeedbackError(null);
  };

  return (
    <div className={`flex-1 overflow-y-auto p-5 lg:p-6 ${MASKOT_RANNA}`}>
      <Maskot vem="elvira" bubbla="klistra in ett kundmejl, så skriver jag ett svarsförslag grundat i kunskapsbasen." />
      <div className="max-w-[1400px] mx-auto pb-10 space-y-4">
        <header className="flex items-center justify-between gap-4">
          <p className="text-[12.5px] text-ink-500">
            Mail-assistenten · klistra in ett kundmail så skriver Elvira ett färdigt svar grundat i kunskapsbasen
          </p>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-ink-500 shrink-0">
            <Sparkles size={12} strokeWidth={2} />
            <span>Justerbar ton · citations</span>
          </div>
        </header>

        <div className="flex">
          <MailTabs value={tab} onChange={setTab} />
        </div>

        {tab === 'templates' ? (
          <MailTemplatesView />
        ) : (
        <div className="grid grid-cols-12 gap-5">
          {/* Input — wider */}
          <div className="col-span-12 lg:col-span-5 xl:col-span-5">
            <Card variant="glass" className="p-6 flex flex-col min-h-[640px] h-full">
              <div className="flex items-center gap-3 mb-4">
                <IconTile icon={<Mail size={14} strokeWidth={2.25} />} tone="brand" size="sm" />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500">
                  Kundmail
                </span>
                <span className="ml-auto text-[10px] font-semibold text-ink-500 tabular-nums">
                  {customerEmail.length > 0 && `${customerEmail.length} tecken`}
                </span>
              </div>

              <textarea
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Dear El-kretsen,&#10;&#10;I am supporting a UK client with producer responsibility compliance in Sweden..."
                className={cn(
                  'flex-1 resize-none min-h-[420px] bg-surface border border-ink-200 rounded-2xl px-5 py-4',
                  'text-[14px] font-medium text-ink-900 placeholder:text-ink-500 leading-relaxed',
                  'focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all',
                )}
                disabled={loading}
              />

              <div className="mt-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 p-1 rounded-xl bg-ink-100/60 border border-ink-100 shadow-inner-soft">
                    <div className="flex items-center gap-1.5 px-2 text-ink-500">
                      <Languages size={14} strokeWidth={2.25} />
                      <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">Svar</span>
                    </div>
                    <button
                      onClick={() => setLanguage('sv')}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all',
                        language === 'sv' ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-600 hover:text-ink-900',
                      )}
                    >
                      Svenska
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all',
                        language === 'en' ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-600 hover:text-ink-900',
                      )}
                    >
                      English
                    </button>
                  </div>

                  <div
                    className="flex items-center gap-1 p-1 rounded-xl bg-ink-100/60 border border-ink-100 shadow-inner-soft"
                    role="radiogroup"
                    aria-label="Kreativitetsnivå"
                  >
                    <div className="flex items-center gap-1.5 px-2 text-ink-500">
                      <Wand2 size={14} strokeWidth={2.25} />
                      <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">Ton</span>
                    </div>
                    {CREATIVITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setCreativity(opt.value)}
                        title={opt.description}
                        role="radio"
                        aria-checked={creativity === opt.value}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all',
                          creativity === opt.value
                            ? 'bg-surface text-brand-700 shadow-sm'
                            : 'text-ink-600 hover:text-ink-900',
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <Button
                    onClick={generate}
                    disabled={disabled}
                    loading={loading}
                    rightIcon={<ArrowRight size={16} strokeWidth={2.25} />}
                    className="ml-auto"
                    size="lg"
                  >
                    Generera svar
                  </Button>
                </div>

                <p className="text-[11px] text-ink-500 leading-snug pl-1">
                  {CREATIVITY_OPTIONS.find((o) => o.value === creativity)?.description}
                </p>
              </div>
            </Card>
          </div>

          {/* Output — dominant */}
          <div className="col-span-12 lg:col-span-7 xl:col-span-7">
            {!result && !loading && !error && <EmptyHero />}
            {loading && !result && (streamReply ? <StreamingReplyCard text={streamReply} language={language} /> : <LoadingHero />)}
            {error && <ErrorHero message={error} />}

            {result && (
              <div className="space-y-4 animate-slide-up">
                {result.summary && (
                  <Card variant="glass" className="p-5">
                    <div className="flex items-start gap-3">
                      <IconTile icon={<Sparkles size={14} strokeWidth={2.25} />} tone="brand" size="sm" />
                      <div className="flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-700 mb-1">
                          AI:s sammanfattning
                        </p>
                        <p className="text-[13.5px] text-ink-800 leading-relaxed">{result.summary}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {result.usedTemplates.length > 0 && (
                  <Card variant="glass" className="p-5 border-emerald-100 bg-emerald-50/30">
                    <div className="flex items-start gap-3">
                      <IconTile icon={<Link2 size={14} strokeWidth={2.25} />} tone="success" size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-1">
                          Byggde på {result.usedTemplates.length}{' '}
                          {result.usedTemplates.length === 1 ? 'mall' : 'mallar'} du lagt in
                        </p>
                        <p className="text-[12px] text-ink-600 leading-relaxed mb-2.5">
                          Elvira tog ton och struktur från följande sparade stilexempel — fakta kom
                          fortfarande från kunskapsbasen.
                        </p>
                        <ul className="space-y-1.5">
                          {result.usedTemplates.map((t) => (
                            <li
                              key={t.id}
                              className="flex items-center gap-2.5 p-2.5 rounded-xl bg-surface border border-emerald-100"
                            >
                              <FileText size={13} strokeWidth={2} className="text-emerald-600 shrink-0" />
                              <span className="flex-1 min-w-0 text-[12.5px] font-semibold text-ink-700 truncate">
                                {t.preview || 'Sparat stilexempel'}…
                              </span>
                              <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 tabular-nums">
                                {Math.round(t.similarity * 100)} % lik
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                )}

                <Card variant="glass" className="p-6 sm:p-8 relative overflow-hidden">
                  <div
                    className="absolute -top-20 -right-16 w-60 h-60 rounded-full bg-brand-100/30 blur-3xl pointer-events-none"
                    aria-hidden
                  />
                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div className="flex items-center gap-3">
                        <IconTile icon={<Mail size={16} strokeWidth={2.25} />} tone="success" size="md" />
                        <div>
                          <h2 className="text-display text-[18px] text-ink-900 leading-tight">Färdigt mailsvar</h2>
                          <p className="text-[11.5px] font-semibold text-ink-500 mt-1">
                            {result.language === 'sv' ? 'Svenska' : 'English'} · redo att kopiera och skicka
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={reset}
                        leftIcon={<RotateCcw size={14} strokeWidth={2.25} />}
                      >
                        Rensa
                      </Button>
                    </div>

                    <div className="rounded-2xl border border-ink-100 bg-surface shadow-card mb-4">
                      <div className="p-6 whitespace-pre-wrap font-medium text-[14px] leading-relaxed text-ink-800 max-h-[520px] overflow-y-auto">
                        {result.reply}
                      </div>
                    </div>

                    {result.gaps.length > 0 && (
                      <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 mb-4">
                        <div className="flex items-center gap-2 mb-2.5">
                          <TriangleAlert size={14} strokeWidth={2.5} className="text-amber-700" />
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                            Fyll i manuellt innan du skickar ({result.gaps.length})
                          </p>
                        </div>
                        <ul className="space-y-1.5">
                          {result.gaps.map((gap, i) => (
                            <li key={i} className="text-[12.5px] text-amber-900 flex items-start gap-2 leading-relaxed">
                              <Flag size={10} strokeWidth={2.5} className="mt-1.5 shrink-0 text-amber-700" />
                              <span>{gap}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        onClick={copyReply}
                        variant={copied ? 'secondary' : 'primary'}
                        size="lg"
                        leftIcon={copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2.25} />}
                      >
                        {copied ? 'Kopierat till urklipp' : 'Kopiera svar'}
                      </Button>

                      {result.sourceFiles.length > 0 && (
                        <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            Källor
                          </span>
                          {result.sourceFiles.slice(0, 4).map((f) => (
                            <span
                              key={f}
                              className="inline-flex items-center px-2 py-0.5 rounded-md bg-ink-50 border border-ink-100 text-[11px] font-semibold text-ink-600 truncate max-w-[200px]"
                              title={f}
                            >
                              {f.replace(/\.[^/.]+$/, '')}
                            </span>
                          ))}
                          {result.sourceFiles.length > 4 && (
                            <span className="text-[10px] font-bold text-ink-500">
                              +{result.sourceFiles.length - 4} fler
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <FeedbackSection
                      rating={feedbackRating}
                      sent={feedbackSent}
                      submitting={feedbackSubmitting}
                      error={feedbackError}
                      comment={feedbackComment}
                      onComment={setFeedbackComment}
                      onRate={sendFeedback}
                    />

                    <div className="pt-3 border-t border-ink-100">
                      {trainingSaved ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-[12px] font-bold text-emerald-800">
                          <Check size={13} strokeWidth={2.25} />
                          Sparat som stilexempel
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<GraduationCap size={14} strokeWidth={1.75} />}
                          onClick={() => setTrainingOpen(true)}
                        >
                          Spara ditt eget svar som stilexempel
                        </Button>
                      )}
                      <p className="text-[11px] text-ink-500 mt-1.5 leading-relaxed">
                        AI:n lär sig din ton för framtida liknande mail — fakta hämtas fortfarande från kunskapsbasen.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {result && (
              <MailTrainingModal
                open={trainingOpen}
                customerEmail={customerEmail.trim()}
                aiDraft={result.reply}
                language={result.language}
                onClose={() => setTrainingOpen(false)}
                onSubmitted={() => {
                  setTrainingSaved(true);
                  setTimeout(() => setTrainingOpen(false), 2500);
                }}
              />
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function MailTabs({ value, onChange }: { value: MailTab; onChange: (v: MailTab) => void }) {
  const options: Array<{ value: MailTab; label: string; icon: ReactNode }> = [
    { value: 'compose', label: 'Skriv svar', icon: <PenLine size={14} strokeWidth={2} /> },
    { value: 'templates', label: 'Mallar', icon: <Library size={14} strokeWidth={2} /> },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 p-1 rounded-xl bg-ink-100/60 border border-ink-100 shadow-inner-soft">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-[12px] font-bold transition-all whitespace-nowrap',
            value === o.value ? 'bg-surface text-ink-900 shadow-sm' : 'text-ink-600 hover:text-ink-900',
          )}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface MailTemplateRow {
  id: string;
  customer_email: string;
  correct_reply: string;
  language: 'sv' | 'en';
  user_note: string | null;
  created_at: string;
  usage_count: number;
  last_used: string | null;
}

function MailTemplatesView() {
  const [rows, setRows] = useState<MailTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc('list_mail_templates');
      if (error) setError(error.message);
      else setRows((data ?? []) as MailTemplateRow[]);
      setLoading(false);
    })();
  }, []);

  const totalUses = rows.reduce((sum, r) => sum + Number(r.usage_count), 0);
  const usedCount = rows.filter((r) => Number(r.usage_count) > 0).length;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={26} className="text-brand-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card variant="glass" className="p-5">
        <div className="flex items-start gap-3">
          <IconTile icon={<Library size={14} strokeWidth={2.25} />} tone="brand" size="sm" />
          <div className="flex-1">
            <h2 className="font-bold text-ink-900 text-[15px] leading-none">
              Alla stilexempel ({rows.length})
            </h2>
            <p className="text-[12px] font-semibold text-ink-500 mt-1.5 leading-relaxed">
              Varje gång någon sparar sitt eget svar som stilexempel hamnar det här. Elvira
              använder de mest liknande exemplen som mall för ton och struktur. Kolumnen{' '}
              <strong className="text-ink-600">Använd</strong> visar hur många mailsvar som faktiskt
              byggt på varje mall — så ni ser att tillagda ändringar tillämpas.
            </p>
            {rows.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                <StatChip label={`${usedCount} av ${rows.length} mallar har använts`} />
                <StatChip label={`${totalUses} mailsvar byggda på mallar totalt`} />
              </div>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <Card variant="glass" className="p-5 border-red-200 bg-red-50/40">
          <p className="text-[13px] text-red-700 font-medium">Kunde inte hämta mallar: {error}</p>
        </Card>
      )}

      {!error && rows.length === 0 ? (
        <Card variant="glass" className="p-12 text-center">
          <IconTile icon={<GraduationCap size={20} strokeWidth={2} />} tone="neutral" size="lg" />
          <h3 className="text-display text-[18px] text-ink-900 mt-4 mb-2">Inga mallar än</h3>
          <p className="text-[13px] text-ink-500 max-w-md mx-auto leading-relaxed">
            När du genererat ett svar i <strong>Skriv svar</strong> kan du klicka{' '}
            <strong>Spara ditt eget svar som stilexempel</strong> för att lära Elvira din ton. De
            sparade mallarna listas här.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <MailTemplateCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-50 border border-brand-100 text-[11px] font-bold text-brand-700">
      {label}
    </span>
  );
}

function MailTemplateCard({ row }: { row: MailTemplateRow }) {
  const [open, setOpen] = useState(false);
  const used = Number(row.usage_count) > 0;

  return (
    <Card variant="glass" className="overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0',
              used
                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                : 'bg-ink-100 text-ink-600 border border-ink-200',
            )}
          >
            <Link2 size={11} strokeWidth={2.5} />
            Använd {row.usage_count}×
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-ink-50 border border-ink-100 text-[10px] font-bold uppercase tracking-wider text-ink-500 shrink-0">
            {row.language === 'sv' ? 'Svenska' : 'English'}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500 inline-flex items-center gap-1">
            <Clock size={11} strokeWidth={2} />
            Tillagd {formatDate(row.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          {row.last_used && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 inline-flex items-center gap-1">
              · senast använd{' '}
              {formatDate(row.last_used, { day: 'numeric', month: 'short' })}
            </span>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="ml-auto text-[11px] font-bold text-brand-600 hover:text-brand-800 transition-colors shrink-0"
          >
            {open ? 'Dölj' : 'Visa helt'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3.5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">
              Kundmail (trigger)
            </p>
            <p
              className={cn(
                'text-[12.5px] text-ink-600 leading-relaxed whitespace-pre-wrap',
                !open && 'line-clamp-3',
              )}
            >
              {row.customer_email}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">
              Linneas svar (stilen Elvira lär sig)
            </p>
            <p
              className={cn(
                'text-[12.5px] text-ink-800 leading-relaxed whitespace-pre-wrap',
                !open && 'line-clamp-3',
              )}
            >
              {row.correct_reply}
            </p>
          </div>
        </div>

        {row.user_note && open && (
          <div className="mt-3 p-3 rounded-xl bg-ink-50/70 border border-ink-100">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1">
              Intern kommentar
            </p>
            <p className="text-[12.5px] text-ink-600 leading-relaxed">{row.user_note}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function EmptyHero() {
  return (
    <Card variant="glass" className="p-12 text-center min-h-[600px] flex flex-col items-center justify-center">
      <IconTile icon={<Mail size={22} strokeWidth={2.25} />} tone="neutral" size="lg" />
      <h3 className="text-display text-[22px] text-ink-900 mt-5 mb-2">Inget mail att besvara än</h3>
      <p className="text-[14px] text-ink-500 max-w-md leading-relaxed">
        Klistra in ett kundmail till vänster och klicka <strong className="text-ink-800">Generera svar</strong>.
        AI:n läser mailet, söker i kunskapsbasen och skriver ett färdigt svarsförslag med källhänvisningar.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-md">
        {['Producentansvar', 'WEEE-direktivet', 'AR-tjänster', 'Avgifter 2026'].map((ex) => (
          <span
            key={ex}
            className="text-[11px] font-semibold px-3 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-100"
          >
            {ex}
          </span>
        ))}
      </div>
    </Card>
  );
}

function LoadingHero() {
  return (
    <Card variant="glass" className="p-12 text-center min-h-[600px] flex flex-col items-center justify-center">
      <Spinner size={36} className="text-brand-500 mb-5" />
      <p className="text-display text-[17px] text-ink-900">AI:n läser och formulerar svar…</p>
      <p className="text-[12px] text-ink-500 mt-2">
        Söker i kunskapsbasen · grundar varje påstående i källor · tar vanligtvis 5-15 sekunder
      </p>
    </Card>
  );
}

function StreamingReplyCard({ text, language }: { text: string; language: 'sv' | 'en' }) {
  return (
    <Card variant="glass" className="p-6 sm:p-8 relative overflow-hidden min-h-[600px] animate-slide-up">
      <div
        className="absolute -top-20 -right-16 w-60 h-60 rounded-full bg-brand-100/30 blur-3xl pointer-events-none"
        aria-hidden
      />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-5">
          <IconTile icon={<Mail size={16} strokeWidth={2.25} />} tone="brand" size="md" />
          <div>
            <h2 className="text-display text-[18px] text-ink-900 leading-tight">Elvira skriver svaret…</h2>
            <p className="text-[11.5px] font-semibold text-ink-500 mt-1 inline-flex items-center gap-1.5">
              <Spinner size={11} className="text-brand-500" />
              {language === 'sv' ? 'Svenska' : 'English'} · grundar varje påstående i källor
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-ink-100 bg-surface shadow-card">
          <div className="p-6 whitespace-pre-wrap font-medium text-[14px] leading-relaxed text-ink-800 max-h-[520px] overflow-y-auto">
            {text}
            <span
              aria-hidden
              className="inline-block w-[2px] h-[1.05em] ml-0.5 -mb-[2px] bg-brand-500 animate-pulse align-middle"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

function ErrorHero({ message }: { message: string }) {
  return (
    <Card variant="glass" className="p-10 min-h-[400px] flex flex-col items-center justify-center text-center">
      <IconTile icon={<TriangleAlert size={18} strokeWidth={2.25} />} tone="danger" size="lg" />
      <h3 className="text-display text-[18px] text-ink-900 mt-5 mb-2">Något gick fel</h3>
      <p className="text-[13px] text-red-700 max-w-md font-medium leading-relaxed">{message}</p>
    </Card>
  );
}

function FeedbackSection({
  rating,
  sent,
  submitting,
  error,
  comment,
  onComment,
  onRate,
}: {
  rating: 'up' | 'down' | null;
  sent: boolean;
  submitting: boolean;
  error: string | null;
  comment: string;
  onComment: (s: string) => void;
  onRate: (r: 'up' | 'down', commentOverride?: string) => void;
}) {
  const showCommentBox = rating === 'down' && !sent;

  if (sent) {
    return (
      <div className="mt-5 pt-4 border-t border-ink-100 flex items-center gap-2.5 text-[12.5px] text-ink-600">
        <Check size={14} strokeWidth={2.5} className="text-emerald-600" />
        <span>
          Tack för feedbacken! {rating === 'up' ? 'Kul att svaret var bra.' : 'Vi lär oss av det som inte funkade.'}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4 border-t border-ink-100 space-y-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
          Var svaret användbart?
        </span>
        <button
          onClick={() => onRate('up')}
          disabled={submitting}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-bold transition-all',
            rating === 'up'
              ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
              : 'bg-surface border-ink-200 text-ink-600 hover:border-emerald-300 hover:text-emerald-700',
            submitting && 'opacity-60 cursor-not-allowed',
          )}
          aria-label="Bra svar"
        >
          <ThumbsUp size={13} strokeWidth={2.25} />
          Bra svar
        </button>
        <button
          onClick={() => onRate('down')}
          disabled={submitting}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-bold transition-all',
            rating === 'down'
              ? 'bg-red-100 border-red-300 text-red-800'
              : 'bg-surface border-ink-200 text-ink-600 hover:border-red-300 hover:text-red-700',
            submitting && 'opacity-60 cursor-not-allowed',
          )}
          aria-label="Dåligt svar"
        >
          <ThumbsDown size={13} strokeWidth={2.25} />
          Dåligt svar
        </button>
      </div>

      {showCommentBox && (
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={(e) => onComment(e.target.value)}
            placeholder="Vad saknades eller var felaktigt? (valfritt — hjälper oss förbättra)"
            rows={3}
            className="w-full resize-none bg-surface border border-ink-200 rounded-xl px-4 py-3 text-[13px] text-ink-800 placeholder:text-ink-500 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all"
            disabled={submitting}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() => onRate('down')}
              loading={submitting}
            >
              Skicka feedback
            </Button>
            <span className="text-[11px] text-ink-500">
              Eller lämna tomt och klicka Dåligt svar igen
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="text-[12px] text-red-700 font-medium">Kunde inte skicka feedback: {error}</p>
      )}
    </div>
  );
}
